import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import dayjs from "dayjs";
import { format } from "d3-format";
import styled from "styled-components";

import { prettyPercentChange } from "../helpers/utils";
import microApi from "../helpers/microApi";

const TooltipWrapper = styled.div`
  padding: 0.5em;
  border: solid var(--gray-d-40) 1px;
  background: #fff;
  line-height: 1.8em;
  font-variant-numeric: tabular-nums;
`;

const TooltipHeading = styled.div`
  font-weight: 500;
`;

const TooltipContent = ({ active, payload, label, timeframe }) => (
  <TooltipWrapper>
    <TooltipHeading>
      {dayjs(label).format("MMMM" + (timeframe === "month" ? "" : " D"))}
    </TooltipHeading>
    {payload.map((p, i) => <div key={i}>
      <span style={{ color: p.stroke }}>
        {dayjs(p.payload.label).subtract(i, "year").format("YYYY")}
      </span>
      {" "}: {p.value.toLocaleString()}
    </div>)}
    {payload.length ? <div>
      % Difference : {prettyPercentChange(payload[0].payload.lastYear, payload[0].payload.curr)}
    </div> : null}
  </TooltipWrapper>
);

const Timeseries = ({ filters, timeframe, latestDay, nation }) => {
  const [data, setData] = useState();

  useEffect(() => {
    let cancel = false;
    const getData = async () => {
      const currWhen = {}, yearAgoWhen = {};

      if (timeframe === "month") {
        currWhen.start = dayjs(latestDay).subtract(12, "month").format("YYYY-MM");
        currWhen.end = dayjs(latestDay).subtract(1, "month").format("YYYY-MM");
        yearAgoWhen.start = dayjs(latestDay).subtract(24, "month").format("YYYY-MM");
        yearAgoWhen.end = dayjs(latestDay).subtract(13, "month").format("YYYY-MM");
      } else {
        if (timeframe === "day") {
          currWhen.start = dayjs(latestDay).subtract(7, "day").format("YYYY-MM-DD");
          currWhen.end = latestDay;
        } else if (timeframe === "week") {
          currWhen.start = dayjs(latestDay).subtract(30, "day").format("YYYY-MM-DD");
          currWhen.end = latestDay;
        }
        yearAgoWhen.start = dayjs(currWhen.start).subtract(365, "day").format("YYYY-MM-DD");
        yearAgoWhen.end = dayjs(currWhen.end).subtract(365, "day").format("YYYY-MM-DD");
      }

      const res = await Promise.all([currWhen, yearAgoWhen]
        .filter(Boolean)
        .map(when => microApi.post(`${nation}/timeseries${nation === "us" ? "?soc_version=soc_emsi_2019" : ""}`, {
          filter: { when, ...filters }
        }))
      );

      const timeframes = res.map(r => r.data.data.timeseries);
      
      if (cancel) return;
      setData({
        data: timeframes[0].unique_postings.map((unique_postings, i) => ({
          label: (timeframes[0].day || timeframes[0].month)[i],
          curr: unique_postings,
          lastYear: timeframes[1] && timeframes[1].unique_postings[i]
        })),
        timeframe
      });
    };
    getData();
    return () => cancel = true;
  }, [filters, timeframe, latestDay, nation]);

  return data ? (
    <ResponsiveContainer width="100%" aspect={2}>
      <LineChart
        data={data.data}
        margin={{
          top: 5,
          right: 40,
          left: 0,
          bottom: 5
        }}
      >
        <CartesianGrid horizontal={false} strokeDasharray="10 10" />
        <XAxis
          dataKey="label"
          tickFormatter={label => dayjs(label).format("MMM" + (data.timeframe === "month" ? "" : " D"))}
          tickSize={12}
          tick={{ fontSize: 14 }}
          tickLine={{ strokeDasharray: "0 6 6" }}
        />
        <YAxis
          domain={["auto", "auto"]}
          tickFormatter={value => format("~s")(value)}
          tickSize={12}
          tick={{ fontSize: 14 }}
          tickLine={{ strokeDasharray: "0 6 6" }}
        />
        <Tooltip
          content={<TooltipContent timeframe={timeframe} />}
        />
        <Legend wrapperStyle={{ bottom: -8 }} />
        <Line
          type="monotone"
          name={data.timeframe === "month" ? "Current 12 Months" : "2020 Postings"}
          animationDuration={200}
          dataKey="curr"
          stroke="#4D88FF"
          strokeWidth={3}
          dot={false}
          activeDot={{ r: 6 }}
        />
        <Line
          type="monotone"
          name={data.timeframe === "month" ? "Previous 12 Months" : "2019 Postings"}
          animationDuration={200}
          dataKey="lastYear"
          stroke="#CA61FF"
          strokeWidth={3}
          activeDot={{ r: 5 }}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  ) : null;
};

export default Timeseries;
